// ============================================================
// EraX API Configuration
// ============================================================

// Detect environment
const isProduction = process.env.NODE_ENV === 'production';

// Get API URL from environment variables
const configuredApiUrl = process.env.REACT_APP_API_URL?.trim();


// ============================================================
// API URL VALIDATION
// ============================================================

const getApiBaseUrl = () => {
  // Production MUST have REACT_APP_API_URL configured
  if (isProduction && !configuredApiUrl) {
    throw new Error(
      'REACT_APP_API_URL is missing. Please configure the production API URL.'
    );
  }

  // Development can safely use localhost
  const url = configuredApiUrl || 'http://localhost:5000';

  let parsedUrl;

  try {
    parsedUrl = new URL(url);
  } catch (error) {
    throw new Error(
      `Invalid REACT_APP_API_URL: "${url}"`
    );
  }

  // Production API must use HTTPS
  if (isProduction && parsedUrl.protocol !== 'https:') {
    throw new Error(
      'Production API URL must use HTTPS.'
    );
  }

  // Prevent credentials from accidentally being included
  if (parsedUrl.username || parsedUrl.password) {
    throw new Error(
      'API URL must not contain a username or password.'
    );
  }

  // Remove trailing slash
  return `${parsedUrl.origin}${parsedUrl.pathname}`.replace(/\/+$/, '');
};


// ============================================================
// FINAL API BASE URL
// ============================================================

const API_BASE = getApiBaseUrl();


// ============================================================
// DEVELOPMENT LOGGING
// ============================================================

if (!isProduction) {
  console.log('🔗 API Base URL:', API_BASE);
  console.log('🌍 Environment:', process.env.NODE_ENV);
}


// ============================================================
// URL HELPER
// ============================================================

const api = (path) => {
  if (typeof path !== 'string') {
    throw new TypeError('API path must be a string.');
  }

  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
};


// ============================================================
// URL PARAMETER ENCODER
// ============================================================

const encode = (value) => {
  return encodeURIComponent(String(value));
};


// ============================================================
// API ENDPOINTS
// ============================================================

export const API_ENDPOINTS = {

  // ----------------------------------------------------------
  // Health Check
  // ----------------------------------------------------------

  HEALTH:
    api('/api/health'),


  // ----------------------------------------------------------
  // Auth & Identity
  // ----------------------------------------------------------

  REGISTER:
    api('/api/identity/register'),

  LOGIN:
    api('/api/identity/login'),

  VERIFY_OTP:
    api('/api/identity/verify-otp'),

  RESEND_OTP:
    api('/api/identity/resend-otp'),

  GOOGLE_SIGNIN:
    api('/api/identity/google-signin'),

  SET_PASSWORD:
    api('/api/identity/set-password'),

  CHECK_EMAIL: (email) =>
    api(
      `/api/identity/check-email?email=${encode(email)}`
    ),


  // ----------------------------------------------------------
  // Referrals
  // ----------------------------------------------------------

  VALIDATE_REFERRAL: (code) =>
    api(
      `/api/identity/validate-referral/${encode(code)}`
    ),

  GET_REFERRAL_CODE: (email) =>
    api(
      `/api/identity/my-referral-code/${encode(email)}`
    ),

  GET_REFERRAL_STATS: (email) =>
    api(
      `/api/identity/referral-stats/${encode(email)}`
    ),


  // ----------------------------------------------------------
  // Profile
  // ----------------------------------------------------------

  GET_PROFILE: (email) =>
    api(
      `/api/identity/profile?email=${encode(email)}`
    ),

  UPDATE_PROFILE:
    api('/api/identity/update-profile'),

  UPDATE_EMAIL:
    api('/api/identity/update-email'),

  UPDATE_PASSWORD:
    api('/api/identity/update-password'),

  DELETE_ACCOUNT:
    api('/api/identity/delete'),

  REQUEST_EMAIL_CHANGE:
    api('/api/identity/request-email-change'),

  VERIFY_EMAIL_CHANGE:
    api('/api/identity/verify-email-change'),

  UPLOAD_AVATAR:
    api('/api/identity/upload-avatar'),

  DELETE_AVATAR:
    api('/api/identity/delete-avatar'),


  // ----------------------------------------------------------
  // Dashboard
  // ----------------------------------------------------------

  DASHBOARD_METRICS: (email) =>
    api(
      `/api/identity/dashboard-metrics?email=${encode(email)}`
    ),


  // ----------------------------------------------------------
  // Investments
  // ----------------------------------------------------------

  CREATE_INVESTMENT:
    api('/api/investment/create'),

  GET_INVESTMENTS: (email) =>
    api(
      `/api/investment/user/${encode(email)}`
    ),

  COMPLETE_DAILY_TASK: (id) =>
    api(
      `/api/investment/complete-daily-task/${encode(id)}`
    ),

  CLAIM_WITH_CODE:
    api('/api/investment/claim-with-code'),

  GET_CLAIM_CODE: (id) =>
    api(
      `/api/investment/claim-code/${encode(id)}`
    ),

  EARLY_WITHDRAWAL: (id) =>
    api(
      `/api/investment/early-withdrawal/${encode(id)}`
    ),


  // ----------------------------------------------------------
  // Deposits
  // ----------------------------------------------------------

  CREATE_DEPOSIT:
    api('/api/deposit/create'),

  GET_DEPOSITS: (email) =>
    api(
      `/api/deposit/user/${encode(email)}`
    ),


  // ----------------------------------------------------------
  // Withdrawals
  // ----------------------------------------------------------

  CREATE_WITHDRAWAL:
    api('/api/withdrawal/create'),

  GET_WITHDRAWALS: (email) =>
    api(
      `/api/withdrawal/user/${encode(email)}`
    )
};


// ============================================================
// EXPORTS
// ============================================================

export { API_BASE };

export const API_BASE_URL = API_BASE;

export default API_BASE;